/**
 * In-app notification types that refer to a unified booking detail screen.
 */
const BOOKING_DETAIL_NOTIFICATION_TYPES = new Set([
  'new_booking_request',
  'booking_confirmed',
  'booking_rejected',
  'booking_cancelled',
  'booking_cancelled_by_client',
  'booking_rescheduled',
  'booking_video_link_added',
  'booking_details_updated',
  'booking_reschedule_request',
  'reschedule_confirmed_by_client',
  'reschedule_rejected_by_client',
  'booking_completed',
  'change_request',
  'change_request_approved',
  'change_request_rejected',
  'team_member_assigned',
]);

/**
 * Resolves where the app should navigate when a notification is activated.
 * Handles legacy full URLs, wrong /bookings/ paths for suppliers, and missing actionUrl when data.bookingId exists.
 *
 * @param {object} notification
 * @param {{ isSupplier?: boolean }} options
 * @returns {string|null} in-app path, full external URL, or null
 */
export function resolveNotificationNavigateTarget(notification, { isSupplier = false } = {}) {
  if (!notification) return null;

  let raw = notification.actionUrl;
  const data = notification.data || {};
  const bookingId =
    data.bookingId ||
    (data.booking && (data.booking._id || data.booking)) ||
    null;
  const agreementId = data.agreementId != null ? String(data.agreementId) : '';
  const id = bookingId != null ? String(bookingId) : '';

  if (!raw && agreementId && AGREEMENT_DETAIL_NOTIFICATION_TYPES.has(notification.type)) {
    raw = isSupplier ? `/supplier/agreements/${agreementId}` : null;
  }
  if (!raw && id && BOOKING_DETAIL_NOTIFICATION_TYPES.has(notification.type)) {
    raw = isSupplier ? `/supplier/bookings/${id}` : `/bookings/${id}`;
  }
  if (!raw) return null;

  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    try {
      const u = new URL(raw);
      if (typeof window !== 'undefined' && u.origin === window.location.origin) {
        raw = `${u.pathname}${u.search}${u.hash}`;
      } else {
        return raw;
      }
    } catch {
      return raw;
    }
  }

  if (isSupplier && id && BOOKING_DETAIL_NOTIFICATION_TYPES.has(notification.type)) {
    const m = raw.match(/^\/bookings\/([^/]+)\/?$/);
    if (m) {
      return `/supplier/bookings/${m[1]}`;
    }
  }

  return raw;
}

const AGREEMENT_DETAIL_NOTIFICATION_TYPES = new Set([
  'agreement_signed'
]);

/** @returns {boolean} true if navigation was performed */
export function navigateFromNotification(navigate, notification, options = {}) {
  const target = resolveNotificationNavigateTarget(notification, options);
  if (!target) return false;
  if (target.startsWith('http://') || target.startsWith('https://')) {
    window.location.href = target;
    return true;
  }
  navigate(target);
  return true;
}
